import { NavLink, useLocation } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import type { SvgIconComponent } from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import type { PermissionCode } from '../types';

export const DRAWER_WIDTH = 248;
export const DRAWER_WIDTH_COLLAPSED = 64;

interface NavItem {
  label: string;
  to: string;
  icon: SvgIconComponent;
  /** Cần ít nhất một trong các quyền này (bỏ trống = luôn hiện). */
  permissions?: PermissionCode[];
  /** Chỉ hiện cho role này (vd: Admin). */
  requireRole?: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Bảng điều khiển', to: '/dashboard', icon: DashboardIcon },
  { label: 'Dự án', to: '/projects', icon: FolderIcon, permissions: ['PROJECT_VIEW'] },
  {
    label: 'Yêu cầu thay đổi',
    to: '/change-requests',
    icon: ChangeCircleIcon,
    permissions: ['CHANGE_REQUEST_VIEW', 'CHANGED_REQUEST_VIEW'],
  },
  { label: 'Lỗi (bug)', to: '/bugs', icon: BugReportIcon, permissions: ['BUG_VIEW'] },
  {
    label: 'Yêu cầu chức năng',
    to: '/feature-requests',
    icon: LightbulbIcon,
    permissions: ['FEATURE_REQUEST_VIEW'],
  },
  {
    label: 'Quản trị',
    to: '/admin',
    icon: AdminPanelSettingsIcon,
    requireRole: 'Admin',
  },
];

interface SidebarProps {
  open: boolean;
  isMobile: boolean;
  onClose: () => void;
}

export function Sidebar({ open, isMobile, onClose }: SidebarProps): JSX.Element {
  const { permissions, roles } = useAuth();
  const location = useLocation();

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.requireRole && !roles.includes(item.requireRole)) return false;
    if (item.permissions && !item.permissions.some((p) => permissions.has(p))) return false;
    return true;
  });

  const width = open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED;

  const content = (
    <Box role="navigation" aria-label="Điều hướng chính">
      {!isMobile && <Toolbar />}
      <List sx={{ px: 1 }}>
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const selected =
            location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          const button = (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={selected}
              onClick={isMobile ? onClose : undefined}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                minHeight: 48,
                justifyContent: open || isMobile ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open || isMobile ? 2 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <Icon />
              </ListItemIcon>
              {(open || isMobile) && <ListItemText primary={item.label} />}
            </ListItemButton>
          );
          return open || isMobile ? (
            button
          ) : (
            <Tooltip title={item.label} placement="right" key={item.to}>
              {button}
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {content}
    </Drawer>
  );
}
